"use strict";

const CustomLayerConfig = function () {
  var _getEsriMetadata = function (url, token) {
    const params = { f: "json" };

    if (token) {
      params.token = token;
    }

    return new Promise((resolve, reject) => {
      L.esri.request(url, params, function (error, metadata) {
        if (error) reject(error);
        else resolve(metadata);
      });
    });
  };

  var getCustomLayerTokenIfNeeded = async function () {
    var token = null;
    if (Tree.get("customLayerToken") !== null && Tree.get("customLayerToken") !== undefined) {
      token = Tree.get("customLayerToken");
    } else {
      token = await ApiCalls.getCustomLayerToken();
      Tree.set("customLayerToken", token);
    }

    return token;
  };

  var parseMapServerUrl = function (serviceUrl) {
    const sublayerMatch = serviceUrl.match(/^(.*\/MapServer)\/(\d+)$/);
    return {
      baseUrl: sublayerMatch ? sublayerMatch[1] : serviceUrl,
      sublayerId: sublayerMatch ? parseInt(sublayerMatch[2]) : null,
    };
  };

  // A service root responds with a `layers` array and no geometry of its own, while a single
  // layer responds with its own `geometryType` and `drawingInfo`.
  var isServiceRootMetadata = function (metadata) {
    return Array.isArray(metadata.layers) && metadata.geometryType === undefined;
  };

  var getCustomLayerMetadata = async function (layerSettings) {
    // The cache records the URL it was fetched from so that editing a layer's URL in settings
    // doesn't leave the map and legend rendering the previous service.
    const cachedMetadata = Tree.get(["customLayerMetadata", layerSettings.layerName]);
    if (cachedMetadata?.serviceUrl === layerSettings.serviceUrl) {
      return cachedMetadata;
    }

    const token = await getCustomLayerTokenIfNeeded();
    const metadata = await _getEsriMetadata(layerSettings.serviceUrl, token);

    if (isServiceRootMetadata(metadata)) {
      metadata.sublayers = await _getSublayerMetadata(layerSettings.serviceUrl, metadata, token);
    }
    metadata.serviceUrl = layerSettings.serviceUrl;

    Tree.set(["customLayerMetadata", layerSettings.layerName], metadata);

    return metadata;
  };

  var _getSublayerMetadata = function (serviceUrl, serviceMetadata, token) {
    return Promise.all(
      serviceMetadata.layers.map(async (sublayer) => ({
        id: sublayer.id,
        name: sublayer.name,
        metadata: await _getEsriMetadata(`${serviceUrl}/${sublayer.id}`, token),
      })),
    );
  };

  var getSublayerSettings = function (layerSettings, sublayer) {
    return {
      ...layerSettings,
      serviceUrl: `${layerSettings.serviceUrl}/${sublayer.id}`,
      layerName: sublayer.name,
    };
  };

  var getCustomFeatureLayerByType = async function (layerSettings) {
    const token = await getCustomLayerTokenIfNeeded();
    const metadata = await getCustomLayerMetadata(layerSettings);

    if (metadata.sublayers) {
      return await _getSublayerGroup(layerSettings, metadata.sublayers, token);
    }

    return await _getFeatureLayerByGeometry(layerSettings, metadata, token);
  };

  var _getSublayerGroup = async function (layerSettings, sublayers, token) {
    const sublayerLayers = await Promise.all(
      sublayers.map((sublayer) =>
        _getFeatureLayerByGeometry(
          getSublayerSettings(layerSettings, sublayer),
          sublayer.metadata,
          token,
        ),
      ),
    );

    return L.layerGroup(sublayerLayers);
  };

  var _getFeatureLayerByGeometry = async function (layerSettings, metadata, token) {
    const geometryType = metadata.geometryType;

    if (
      ["esriGeometryPolygon", "esriGeometryLine", "esriGeometryPolyline"].includes(geometryType)
    ) {
      return await Layers.customPolyLayerWithDrawingInfo(layerSettings, token, metadata);
    } else if (["esriGeometryPoint", "esriGeometryMultipoint"].includes(geometryType)) {
      return await Layers.customPointLayerWithDrawingInfo(layerSettings, token, metadata);
    }

    throw new Error(
      `Custom layer "${layerSettings.layerName}" at ${layerSettings.serviceUrl} has unsupported geometry type "${geometryType}".`,
    );
  };

  return {
    getCustomFeatureLayerByType,
    getCustomLayerMetadata,
    getCustomLayerTokenIfNeeded,
    getSublayerSettings,
    isServiceRootMetadata,
    parseMapServerUrl,
  };
};

module.exports = CustomLayerConfig();

const Layers = require("./layers");
const Tree = require("../tree");
const ApiCalls = require("../apiCalls");
